import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";

import { PropertyExtractionService } from "./property-extraction.service";

/** Proveniência de um controlo do formulário preenchido por IA. */
export interface AiFieldProvenance {
  /** Separador do formulário: details, address, areasNearby. */
  tab: string;
  /** Nome do controlo (formControlName). */
  control: string;
  /** Valor aplicado. O badge só aparece enquanto o controlo tiver este valor. */
  value: any;
  documentName: string | null;
  page: number | null;
  confidence: number | null;
}

export interface AiPendingApply {
  runId: number;
  appliedFieldKeys: string[];
}

/**
 * Estado da extração por IA que tem de sobreviver ao componente do formulário:
 *  - o run revisto que ainda falta ligar ao imóvel (só se sabe o id do imóvel
 *    depois de gravar, e o modal destrói o componente logo a seguir);
 *  - a proveniência dos campos, para os badges "IA".
 */
@Injectable({ providedIn: "root" })
export class AiExtractionStateService {
  private pending: AiPendingApply | null = null;
  private readonly provenance$ = new BehaviorSubject<AiFieldProvenance[]>([]);

  constructor(private extraction: PropertyExtractionService) {}

  get provenance(): AiFieldProvenance[] {
    return this.provenance$.value;
  }

  observeProvenance() {
    return this.provenance$.asObservable();
  }

  setProvenance(fields: AiFieldProvenance[]) {
    this.provenance$.next(fields ?? []);
  }

  /** Junta proveniência nova à existente; o mais recente ganha por controlo. */
  mergeProvenance(fields: AiFieldProvenance[]) {
    const byControl = new Map<string, AiFieldProvenance>();
    for (const field of this.provenance$.value) {
      byControl.set(`${field.tab}|${field.control}`, field);
    }
    for (const field of fields ?? []) {
      byControl.set(`${field.tab}|${field.control}`, field);
    }
    this.provenance$.next(Array.from(byControl.values()));
  }

  /** Guarda o run revisto até o imóvel ser gravado. */
  setPending(pending: AiPendingApply | null) {
    this.pending = pending;
  }

  get hasPending(): boolean {
    return !!this.pending;
  }

  /**
   * Chamado quando o imóvel foi gravado com sucesso. Liga o run ao imóvel: o
   * servidor anexa os documentos e regista a proveniência. Uma falha aqui não
   * afeta a gravação do imóvel, que já aconteceu.
   */
  commit(propertyId: string | null | undefined) {
    const pending = this.pending;
    if (!pending || !propertyId) return;
    this.pending = null;

    this.extraction
      .apply(pending.runId, {
        propertyId,
        appliedFieldKeys: pending.appliedFieldKeys,
      })
      .subscribe({ error: () => undefined });
  }

  /** Limpa tudo ao fechar o formulário do imóvel. */
  reset() {
    this.pending = null;
    this.provenance$.next([]);
  }
}
